import { useCallback } from 'react';
import type { Href } from 'expo-router';

import { useSetLastInteractedMenuStore } from '../../../../context/LastInteractedStore';
import { useCart, useNavigateToMenu, type UseNavigateToMenuProps } from '../../../../hooks';
import { useConflictReview, useReorderState } from '../../../../screens/ReorderingScreen';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Navigate to restaurant and register last visited store.
 */
export const useNavigateToMenuFromLocation = (params: Params = {}) => {
  const { backToPath, isInsideModal } = params;

  const navigateToMenu = useNavigateToMenu({ backToPath, isInsideModal });
  const setLastInteractedMenuStore = useSetLastInteractedMenuStore();
  const { currentStep } = useReorderState();
  const conflictReview = useConflictReview();
  const { isCartEmpty, cart } = useCart();

  const restaurantSlug = cart?.restaurant?.slug;
  const addressId = cart?.deliveryOrderDetail?.address?.id;

  const cartRestaurantSlug = isCartEmpty ? undefined : restaurantSlug;
  const cartDeliveryAddressId = isCartEmpty ? undefined : addressId;

  const isReordering = currentStep !== 'idle';

  // ─────────────────────────────────────────────────────────────────────

  return useCallback(
    (props: UseNavigateToMenuProps) => {
      // if you are already reordering then go to the next step in the flow.
      if (isReordering) {
        void conflictReview({
          orderType: props.orderType,
          deliveryAddressName: props.deliveryAddressName,
          deliveryOrderDetail: props.deliveryOrderDetail,
          restaurantId: props.restaurantId,
          restaurantSlug: props.restaurantSlug,
          restaurantName: props.restaurantName,
        });

        return;
      }

      const isSameAddressId = cartDeliveryAddressId === props.deliveryOrderDetail?.addressId;
      const isSameRestaurantSlug = cartRestaurantSlug === props.restaurantSlug;
      const hasBag = Boolean(cartRestaurantSlug) || Boolean(cartDeliveryAddressId);
      const needsToReorder = hasBag && (!isSameAddressId || !isSameRestaurantSlug);

      // if your bag belongs to a different restaurant, start reorder flow
      if (needsToReorder) {
        void conflictReview({
          orderId: cart?.id,
          orderType: props.orderType,
          deliveryAddressName: props.deliveryAddressName,
          deliveryOrderDetail: props.deliveryOrderDetail,
          restaurantId: props.restaurantId,
          restaurantSlug: props.restaurantSlug,
          restaurantName: props.restaurantName,
        });

        return;
      }

      setLastInteractedMenuStore({
        restaurantId: props.restaurantId,
        restaurantName: props.restaurantName,
        restaurantSlug: props.restaurantSlug,
        addressId: props.deliveryOrderDetail?.addressId,
        deliveryFee: props.deliveryOrderDetail?.deliveryFee,
        vendor: props.deliveryOrderDetail?.vendor,
        vendorRestaurantId: props.deliveryOrderDetail?.vendorRestaurantId,
      });
      navigateToMenu(props);
    },
    [
      isReordering,
      cartDeliveryAddressId,
      cartRestaurantSlug,
      navigateToMenu,
      setLastInteractedMenuStore,
      conflictReview,
      cart?.id,
    ],
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  backToPath?: Href;
  isInsideModal?: boolean;
};
